// Copyright (c) Brock Allen & Dominick Baier. All rights reserved.
// Licensed under the Apache License, Version 2.0. See LICENSE in the project root for license information.

import { Logger } from "./utils";
import { SigninState } from "./SigninState";

/**
 * @public
 */
export interface SigninRequestArgs {
    // mandatory
    url: string;
    authority: string;
    app_id: string;
    redirect_uri: string;

    // optional
    prompt?: string;
    display?: string;
    max_age?: number;
    ui_locales?: string;
    id_token_hint?: string;
    login_hint?: string;
    acr_values?: string;
    resource?: string;
    response_mode?: "query" | "fragment" ;
    request?: string;
    request_uri?: string;
    extraQueryParams?: Record<string, string | number | boolean>;
    request_type?: string;
    app_secret?: string;
    sealed_app_id?: string;
    extraTokenParams?: Record<string, unknown>;
    skipUserInfo?: boolean;

    /** custom "state", which can be used by a caller to have "data" round tripped */
    state_data?: unknown;
}

/**
 * @public
 */
export class SigninRequest {
    private readonly _logger = new Logger("SigninRequest");

    public readonly url: string;
    public readonly state: SigninState;

    public constructor({
        // mandatory
        url, authority, app_id, redirect_uri,
        // optional
        state_data, response_mode, request_type, app_secret, sealed_app_id,
        skipUserInfo,
        extraQueryParams,
        extraTokenParams,
        ...optionalParams
    }: SigninRequestArgs) {
        if (!url) {
            this._logger.error("ctor: No url passed");
            throw new Error("url");
        }
        if (!app_id) {
            this._logger.error("ctor: No app_id passed");
            throw new Error("client_id");
        }
        if (!redirect_uri) {
            this._logger.error("ctor: No redirect_uri passed");
            throw new Error("redirect_uri");
        }
        if (!authority) {
            this._logger.error("ctor: No authority passed");
            throw new Error("authority");
        }

        this.state = new SigninState({
            data: state_data,
            request_type,
            app_id, sealed_app_id, authority, redirect_uri,
            response_mode,
            app_secret, extraTokenParams,
            skipUserInfo,
        });

        const parsedUrl = new URL(url);
      
        parsedUrl.searchParams.append("returnUrl", redirect_uri);

        if (this.state.sealed_app_id) {
            parsedUrl.searchParams.append("appId", this.state.sealed_app_id);
        }
        else {
            parsedUrl.searchParams.append("appId", app_id);
        }

        this.url = parsedUrl.href;
    }
}
